import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { BackOfficeService } from '../../../../../services/backoffice.service';

@Component({
  selector: 'app-problem-orders-modal',
  standalone: false,
  templateUrl: './problem-orders-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./problem-orders-modal.component.css']
})
export class ProblemOrdersModalComponent {
  readonly i18n = {
    title: { en: 'Problem Orders', sv: 'Problemordrar' },
    noOrders: { en: 'No problem orders', sv: 'Inga problemordrar' },
    close: { en: 'Close', sv: 'Stäng' },
    solve: { en: 'Solved', sv: 'Löst' },
    notSolved: { en: 'This order does not look solved yet', sv: 'Ordern verkar inte vara löst än' },
    snackClose: { en: 'Close', sv: 'Stäng' },
  };

  solvingOrderIds = new Set<string>();

  private readonly problemFlagLabels: { flag: number, label: { en: string, sv: string } }[] = [
    { flag: 1, label: { en: 'Not checked in', sv: 'Ej incheckad' } },
    { flag: 2, label: { en: 'Not checked out', sv: 'Ej utcheckad' } },
    { flag: 4, label: { en: 'Key not returned', sv: 'Nyckel ej återlämnad' } },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      problemOrders: { orderId: string, orderNr: number | null, carId: string | null, problemFlags: number }[],
      onOrderSolved?: (orderId: string) => void
    },
    private dialogRef: MatDialogRef<ProblemOrdersModalComponent>,
    private router: Router,
    private backOfficeService: BackOfficeService,
    private snackBar: MatSnackBar) { }

  getProblemLabels(problemFlags: number): string[] {
    return this.problemFlagLabels
      .filter(p => (problemFlags & p.flag) === p.flag)
      .map(p => p.label.en);
  }

  goToOrder(orderId: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${orderId}`]));
    window.open(url, '_blank');
  }

  solveOrder(order: { orderId: string, orderNr: number | null, carId: string | null, problemFlags: number }, event: Event): void {
    event.stopPropagation();
    if (this.solvingOrderIds.has(order.orderId)) return;

    this.solvingOrderIds.add(order.orderId);
    this.backOfficeService.checkIsOrderProblemSolved(order.orderId).subscribe({
      next: (solved: boolean) => {
        this.solvingOrderIds.delete(order.orderId);

        if (!solved) {
          this.snackBar.open(
            this.i18n.notSolved.en,
            this.i18n.snackClose.en,
            { duration: 4000 }
          );
          return;
        }

        const index = this.data.problemOrders.indexOf(order);
        if (index > -1) this.data.problemOrders.splice(index, 1);
        this.data.onOrderSolved?.(order.orderId);
      },
      error: () => {
        this.solvingOrderIds.delete(order.orderId);
        this.snackBar.open(
          this.i18n.notSolved.en,
          this.i18n.snackClose.en,
          { duration: 4000 }
        );
      }
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
